import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Download, Upload, Plus, Lock, ExternalLink } from 'lucide-react';
import MaintenanceCalendarWidget from '@/components/dashboard/MaintenanceCalendarWidget';
import CreateMaintenanceModal from '@/components/maintenance/CreateMaintenanceModal';
import { useAuth } from '@/contexts/AuthContext';
import { propertiesService } from '@/services/propertiesService';

const MaintenancePage = () => {
  const { user, loading: authLoading } = useAuth();
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    const fetchProperties = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const data = await propertiesService.getProperties();
        setProperties(data || []);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError('Fehler beim Laden der Immobilien');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchProperties();
    }
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Fehler</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Wartungsmanagement</h1>
          <p className="text-gray-600 dark:text-gray-400">Verwalten Sie Wartungsaufgaben für Ihre Immobilien</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Clara Vault Button - DIRECT LINK */}
          <Button
            onClick={() => window.open('https://vault.clara360.de', '_blank')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Lock className="w-4 h-4 mr-2" />
            Clara Vault
            <ExternalLink className="w-3 h-3 ml-2" />
          </Button>
          
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Wartung
          </Button>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700 dark:text-gray-300">Filter</span>
            </div>
            <div className="flex-1">
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Immobilie auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Immobilien</SelectItem>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id.toString()}>
                      {property.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wartungskalender */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>📅 Wartungskalender</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MaintenanceCalendarWidget selectedProperty={selectedProperty} />
        </CardContent>
      </Card>

      {/* Create Maintenance Modal */}
      <CreateMaintenanceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        properties={properties}
      />
    </div>
  );
};

export default MaintenancePage;

